import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useClients } from "@/contexts/ClientContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Building2, ChevronRight, Pencil, ArrowUpDown, ArrowUp, ArrowDown, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { ClientLogo } from "@/components/ClientLogo";

export default function Clients() {
  const { clients, reload, select } = useClients();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [budgets, setBudgets] = useState({}); // {client_id: {hr, ad, props}}
  const [aiSpend, setAiSpend] = useState({}); // {client_id: {avg_monthly_cost, current_month_cost}}
  const [sort, setSort] = useState({ key: "company", dir: "asc" });

  // Pull the per-client monthly HR + Ad budget roll-up.
  useEffect(() => {
    let cancelled = false;
    api.get("/clients/budget-summary")
      .then(({ data }) => {
        if (cancelled) return;
        const map = {};
        (data || []).forEach((r) => {
          map[r.client_id] = {
            hr: Number(r.hr_budget_monthly || 0),
            ad: Number(r.ad_budget_monthly || 0),
            props: Number(r.property_count || 0),
          };
        });
        setBudgets(map);
      })
      .catch(() => {});
    // Per-client AI spend roll-up (avg monthly + current month).
    api.get("/ai-usage/per-client-summary")
      .then(({ data }) => { if (!cancelled) setAiSpend(data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [clients.length]);

  const sortedClients = React.useMemo(() => {
    const arr = [...(clients || [])];
    const dir = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      if (sort.key === "company") {
        const av = (a.company_name || "").toLowerCase();
        const bv = (b.company_name || "").toLowerCase();
        return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
      }
      if (sort.key === "hr") {
        return ((budgets[a.client_id]?.hr || 0) - (budgets[b.client_id]?.hr || 0)) * dir;
      }
      if (sort.key === "ad") {
        return ((budgets[a.client_id]?.ad || 0) - (budgets[b.client_id]?.ad || 0)) * dir;
      }
      if (sort.key === "props") {
        return ((budgets[a.client_id]?.props || 0) - (budgets[b.client_id]?.props || 0)) * dir;
      }
      if (sort.key === "ai_spend") {
        return ((aiSpend[a.client_id]?.current_month_cost || 0) -
                (aiSpend[b.client_id]?.current_month_cost || 0)) * dir;
      }
      return 0;
    });
    return arr;
  }, [clients, sort, budgets, aiSpend]);

  const toggleSort = (key) => {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        // first click: name → asc; budgets → desc (highest first)
        : { key, dir: key === "company" ? "asc" : "desc" }
    );
  };

  const fmtMoney = (n, decimals = 0) =>
    Number.isFinite(Number(n))
      ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD",
                                         minimumFractionDigits: decimals,
                                         maximumFractionDigits: decimals }).format(Number(n))
      : "—";

  useEffect(() => {
    if (editing) {
      setForm({
        company_name: editing.company_name || "",
        notes: editing.notes || "",
        first_name: editing.contact?.first_name || "",
        last_name: editing.contact?.last_name || "",
        email: editing.contact?.email || "",
        mobile_phone: editing.contact?.mobile_phone || "",
        logo_file: null,
      });
    } else {
      setForm(defaultForm());
    }
  }, [editing]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        company_name: form.company_name,
        notes: form.notes,
        contact: {
          first_name: form.first_name, last_name: form.last_name,
          email: form.email, mobile_phone: form.mobile_phone,
        },
      };
      let cid;
      if (editing) {
        await api.patch(`/clients/${editing.client_id}`, body);
        cid = editing.client_id;
      } else {
        const { data } = await api.post("/clients", body);
        cid = data.client_id;
      }
      if (form.logo_file) {
        const fd = new FormData();
        fd.append("file", form.logo_file);
        await api.post(`/clients/${cid}/logo`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editing ? "Client updated" : "Client created");
      setOpen(false);
      setEditing(null);
      await reload();
      select(cid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/clients/${c.client_id}`);
      toast.success("Client deleted");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const goToClient = (c) => {
    select(c.client_id);
    navigate(`/clients/${c.client_id}`);
  };

  return (
    <div className="space-y-8" data-testid="clients-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="label-overline">Manage</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">Clients</h1>
          <p className="text-sm text-zinc-500 mt-1">Click a row to open the client workspace.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white h-10"
              data-testid="add-client-button">
              <Plus className="h-4 w-4 mr-1" /> Add client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl rounded-sm" data-testid="client-dialog">
            <DialogHeader>
              <DialogTitle className="font-display font-bold text-2xl tracking-tight">
                {editing ? "Edit client" : "New client"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label className="label-overline">Company name</Label>
                <Input required value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  data-testid="client-company-input" className="rounded-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="label-overline">First name</Label>
                  <Input required value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    data-testid="client-firstname-input" className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Last name</Label>
                  <Input required value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    data-testid="client-lastname-input" className="rounded-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="label-overline">Contact email</Label>
                  <Input type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="client-email-input" className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Mobile phone</Label>
                  <Input value={form.mobile_phone}
                    onChange={(e) => setForm({ ...form, mobile_phone: e.target.value })}
                    data-testid="client-phone-input" className="rounded-sm" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Company logo</Label>
                <Input type="file" accept="image/*"
                  onChange={(e) => setForm({ ...form, logo_file: e.target.files?.[0] || null })}
                  data-testid="client-logo-input" className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Internal notes</Label>
                <Textarea rows={3} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="client-notes-input" className="rounded-sm" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy}
                  className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="client-submit-button">
                  {busy ? "Saving…" : (editing ? "Save changes" : "Create client")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* LIST */}
      <Card className="rounded-sm border-zinc-200 overflow-hidden">
        {/* Column headers (sortable) */}
        <div className="grid grid-cols-[64px_2fr_1.4fr_1.2fr_0.8fr_1.1fr_1.1fr_1.3fr_auto] gap-4 px-5 py-3 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          <div></div>
          <SortHeader label="Company" col="company" sort={sort} onClick={toggleSort} />
          <div>Contact</div>
          <div>Email</div>
          <SortHeader label="Properties" col="props" sort={sort} onClick={toggleSort} align="right" />
          <SortHeader label="HR Budget / mo" col="hr" sort={sort} onClick={toggleSort} align="right" />
          <SortHeader label="Ad Budget / mo" col="ad" sort={sort} onClick={toggleSort} align="right" />
          <SortHeader label="AI Spend (avg · MTD)" col="ai_spend" sort={sort} onClick={toggleSort} align="right" />
          <div></div>
        </div>

        <CardContent className="p-0">
          {sortedClients.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
              <div className="font-display font-bold text-lg">No clients yet</div>
              <p className="text-sm text-zinc-500 mt-1">Click &quot;Add client&quot; to create your first company.</p>
            </div>
          ) : (
            sortedClients.map((c) => (
              <div key={c.client_id}
                role="button"
                onClick={() => goToClient(c)}
                className="grid grid-cols-[64px_2fr_1.4fr_1.2fr_0.8fr_1.1fr_1.1fr_1.3fr_auto] gap-4 px-5 py-4 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 cursor-pointer transition-colors items-center"
                data-testid={`client-row-${c.client_id}`}>
                <ClientLogo name={c.company_name} logoUrl={c.logo_url} size={40} />
                <div className="min-w-0">
                  <div className="font-display font-bold text-sm truncate">{c.company_name}</div>
                </div>
                <div className="text-sm text-zinc-700 truncate">
                  {c.contact?.first_name} {c.contact?.last_name}
                </div>
                <div className="text-xs text-zinc-500 truncate">{c.contact?.email}</div>
                <div className="text-right font-mono text-xs text-zinc-800 tabular-nums"
                  data-testid={`property-count-${c.client_id}`}>
                  {budgets[c.client_id]?.props ?? "—"}
                </div>
                <div className="text-right font-mono text-xs text-zinc-800 tabular-nums"
                  data-testid={`hr-budget-${c.client_id}`}>
                  {fmtMoney(budgets[c.client_id]?.hr)}
                </div>
                <div className="text-right font-mono text-xs text-zinc-800 tabular-nums"
                  data-testid={`ad-budget-${c.client_id}`}>
                  {fmtMoney(budgets[c.client_id]?.ad)}
                </div>
                <div className="text-right font-mono text-[11px] tabular-nums leading-tight"
                  data-testid={`ai-spend-${c.client_id}`}
                  title={`Avg monthly AI spend across ${aiSpend[c.client_id]?.months_active || 0} active month(s) · current month-to-date`}>
                  <div className="text-zinc-500">
                    {fmtMoney(aiSpend[c.client_id]?.avg_monthly_cost ?? 0, 2)}<span className="text-zinc-300 mx-1">·</span><span className="text-zinc-900 font-semibold">{fmtMoney(aiSpend[c.client_id]?.current_month_cost ?? 0, 2)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0"
                    title="Onboarding checklist"
                    onClick={() => navigate(`/clients/${c.client_id}/checklist`)}
                    data-testid={`checklist-client-${c.client_id}`}>
                    <ClipboardCheck className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0"
                    onClick={() => { setEditing(c); setOpen(true); }}
                    data-testid={`edit-client-${c.client_id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        data-testid={`delete-client-${c.client_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {c.company_name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This removes all uploads, data, reports, properties and brief documents for this client.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                        <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700"
                          onClick={() => remove(c)} data-testid={`confirm-delete-${c.client_id}`}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <ChevronRight className="h-4 w-4 text-zinc-400 ml-2" />
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function defaultForm() {
  return {
    company_name: "", notes: "",
    first_name: "", last_name: "", email: "", mobile_phone: "",
    logo_file: null,
  };
}

function SortHeader({ label, col, sort, onClick, align = "left" }) {
  const active = sort.key === col;
  const Arrow = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onClick(col)}
      data-testid={`sort-${col}`}
      className={`flex items-center gap-1 ${align === "right" ? "justify-end" : "justify-start"}
                  text-[10px] uppercase tracking-[0.2em] hover:text-[#002FA7] transition-colors
                  ${active ? "text-[#002FA7]" : "text-zinc-500"}`}
      title={`Sort by ${label}`}
    >
      <span>{label}</span>
      <Arrow className={`h-3 w-3 ${active ? "opacity-100" : "opacity-40"}`} />
    </button>
  );
}
